// shared/src/prompts/m-consensus-analyst.ts
// Chain V2 — market consensus path, analyst stage.
// Per design-notes L5/L7/L28/L30.
//
// Mirror of m-contrarian-analyst with consensus framing + consensus-enum
// priced_explanation. Steel-mans the majority view; explains WHY the market
// believes what it believes. Same OutputSchema shape as contrarian (path-
// neutral fields per L30).

import { z } from 'zod';
import { HorizonEnum, ThesisStatusEnum, HighMedLowEnum, RegimeEnum } from '../resolvers/llm-enum-coerce.js';
import { llmStringItem } from '../resolvers/llm-string-coerce.js';
import { llmNumber } from '../resolvers/llm-number-coerce.js';
import { hoistMisnestedFields } from '../resolvers/hoist-misnested.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import { renderNewsBlock } from './_news-render.js';
import { renderRssBlock } from './_rss-render.js';
import { renderUpcomingEconBlock } from '../econ-calendar-param.js';
import { renderMacroGroundingBlock, type MacroGroundTruth } from '../translators/macro-grounding.js';
import { formatToolCatalogForPrompt, formatFredSeriesHint } from '../tool-names.js';
import type { EconCalendarEvent } from '../types/econ-calendar.js';
import type { LlmNewsItem } from '../news/types.js';

export const VERSION = '1.12.0';   // 2026-08-17 #23 — request_tools[].args now documented: non-empty args example + explicit news_search query directive + per-tool TOOL ARG SPECS block (worker-injected). Stops the empty-args fail-soft (NEWS_SEARCH_QUERY_MISSING / COMPUTE_INVALID_PRICES). // Prior: 1.11.0 (2026-07-10 — cascade_nodes .min(1)→.max(12).default([]) (omission-safe; empty→[] + EMPTY_CASCADE WARN badge, not a brief kill)). // Prior: 1.10.0 (2026-06-11 — macro-grounding: GROUNDED MACRO ACTUALS block (labeled Core/Headline CPI + code-derived YoY) + no-consensus guard (rule 14b). Stops the 019eb1bf fabrication ("Core CPI 2.9% vs 4.2% consensus"). See docs/macro-grounding-validator-2026-06-11-brief-revision.md. // Prior: 1.9.0 (2026-06-11 — schema rigidity hardening: probability + regime_confidence wrapped in llmNumber). // Prior: 1.8.0 (2026-06-11 — UPCOMING ECON CALENDAR block). // Prior: 1.7.0 (2026-05-26 #107 — OBSERVABLE = STATE LEVEL + PARTIAL CATALYST FILL ban). Matches contrarian-analyst.
export const STEP    = 'm.consensus.analyst';
export const MODEL   = 'deepseek-v4-flash';
// 2026-07-31 — raised 12000 → 24000 alongside the contrarian sibling. This step
// was the WORSE case: on the 19:00 UTC fire its reasoning_tokens hit 12000, i.e.
// reasoning consumed the ENTIRE budget and left zero content. True demand is
// therefore unknown and above 12000, so the new ceiling is set with real
// headroom rather than just above the observed value. See the contrarian
// module's note for the full rationale (same root cause, same fire).
// Evidence: docs/audits/2026-07-31-chain-truncation/INVESTIGATION.md
// 2026-08-01 — raised 24000 → 32000 to stay symmetric with the contrarian
// sibling (see its note); kept equal so neither path has a structural budget
// edge. docs/audits/2026-08-01-token-cap-headroom-review.md
export const MAX_TOKENS = 32000;
export const ROLE    = 'analyst' as const;
export const DESC    = 'Drafts the consensus macro thesis — steel-mans the prevailing market view' as const;   // #45 registry — one-line operator description

// Path-neutral fields per L30. priced_explanation here uses the CONSENSUS
// enum (structural_flow / hedging_demand / passive_indexing / momentum_chasing
// / rate_anchoring / fed_credibility / crowded_consensus / other) — enforced
// in prompt, not Zod, so the schema mirrors the contrarian shape exactly.
const BaseOutputSchema = z.object({
  thesis_headline:       z.string().min(1),
  thesis_paragraph:      z.string().min(1),
  basis: z.object({
    evidence:            z.string().min(1),
    catalyst:            z.string(),
    invalidation:        z.string(),
  }),
  priced_explanation:    z.string().min(1),
  probability:           llmNumber(z.number().min(0).max(100)),    // D38 (2026-05-28): dropped .int(). 2026-06-11: llmNumber-coerced (string "65"/"50%" → number; range stays).
  // Session 5T (2026-05-28) D13: widened to include '2w' — see m-contrarian-analyst.ts for rationale.
  horizon:               HorizonEnum,
  conditional:           z.string(),
  cascade_prose:         z.string(),
  // Session 5T (2026-05-28) D15: loosened .min(3)→.min(1). Prompt still requests 3-5 typical (normative).
  // 2026-06-23: .max(5)→.max(12). The 5 was a CascadeDiagram display-density spec ("3-5 nodes"), NOT a data
  // cap — the renderer already clamps (CascadeDiagram.resolveCascadeNodes slice 0,5; ZoneThesisRipples chips
  // wrap). A 6-node crash-day cascade hard-failed a whole brief at m.consensus.refine (brief 019ef4b1). 12 is
  // a runaway-junk guard that never trips on real output. See docs/prompt-arbitrary-limits-audit-2026-05-28.md.
  cascade_nodes:         z.array(llmStringItem).max(12).default([]),   // 2026-07-10: omission-safe. Flash can drop the key → whole brief fails; default an omitted/empty key to [] (rule still requests 3-5) and surface an EMPTY_CASCADE WARN badge from the stage instead of a hard brief kill.
  cross_portfolio_thread: z.string(),
  top_risk:              z.string(),
  risk_mit:              z.string(),
  thesis_status:         ThesisStatusEnum,
  thesis_div:            HighMedLowEnum,
  regime:                RegimeEnum,
  regime_confidence:     llmNumber(z.number().min(0).max(1)),
  // Session 5T (2026-05-28) D15: loosened .min(2)→.min(1). See macro-prediction-emit.ts:152.
  macro_predictions:     z.array(z.unknown()).min(1).max(5),
  request_tools:         z.array(z.object({
    tool_name: z.string(),
    args:      z.record(z.unknown()),
  })).optional(),
}).passthrough();

// Dropped-middle-brace repair — see m-contrarian-analyst.ts (identical shape/root
// cause). Lifts any basis-nested top-level field back up before validation; no-op
// on clean output. docs/investigations/m-contrarian-analyst-basis-nesting-2026-08-10.md
export const OutputSchema = z.preprocess(
  (v) => hoistMisnestedFields(v, 'basis', Object.keys(BaseOutputSchema.shape), { step: STEP }),
  BaseOutputSchema,
);
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `# MARKET CONSENSUS ANALYST (V2)

## ROLE
Institutional PM, portfolio overlay. You are the CONSENSUS voice on this brief.
Your job is to STEEL-MAN the prevailing market view. Explain why the desk's majority position is RIGHT — what mechanism justifies it, what payload evidence supports it, what catalyst would CONFIRM it, what would invalidate it.

You are NOT being asked to invent a strawman the contrarian can shred. The contrarian path runs in parallel and is independently making the opposing argument. Your job is to be the BEST advocate for the majority view, predicting observables AS CONSENSUS WOULD if it had to put numbers on the page.

Your draft will be (i) attacked by a consensus-skeptic that ALSO sees the contrarian analyst's draft as steel-man counter, (ii) refined by you given that skeptic critique + tool results, and (iii) adjudicated against the contrarian path by a synthesis LLM. Stake the strongest consensus claim.

## CONTEXT YOU RECEIVE (user message)
- GROUNDING FACTS (today, snapshot age) — for forward-date discipline only
- Full macro / crypto payload
- Per-ticker prices for the universe under analysis
- RSS news + macro news

## MACRO INTERPRETATION (consensus lens)
Frame each indicator AS THE MAJORITY WOULD frame it. Examples:
- VIX low + hy_spread tight → "carry trades persist; goldilocks bid"
- yields rising into Fed pause → "soft-landing priced; growth holds"
- crypto rallying with QT → "structural flow / ETF demand insulates from liquidity"
The consensus position is rarely "no view" — it's an active view with payload-cited justification. Find it.

## OUTPUT (strict JSON; first char='{'; last char='}'; no commentary outside)
{
  "thesis_headline":    "<≤25w punchy consensus headline. The view the desk is positioned for.>",
  "thesis_paragraph":   "<100-120w. Sentence 1 = consensus headline ≤15w. Sentences 2-N = connected mechanism for WHY consensus is right (cite payload field=value, explain the supporting evidence chain, close on the trigger that would CONFIRM the view). NO 'overall'/'in summary', NO bullet lists.>",
  "basis": {
    "evidence":         "<≤14w: payload alignment supporting majority. field_a=value matches field_b=value.>",
    "catalyst":         "<≤12w: forward event that would CONFIRM consensus. MUST be future (≥ TODAY).>",
    "invalidation":     "<≤12w: one falsifiable condition that would BREAK consensus.>"
  },
  "priced_explanation": "<enum>: <≤25w rationale>. Enum: structural_flow | hedging_demand | passive_indexing | momentum_chasing | rate_anchoring | fed_credibility | crowded_consensus | other",
  "probability":        50,
  "horizon":            "1w | 1m | 1q",
  "conditional":        "<≤18w: rises to X% if Y.>",
  "cascade_prose":      "<60-80w: explicit A→B→C chain for consensus playing out, ≥2 distinct mechanisms.>",
  "cascade_nodes":      ["<≤6w label>", "<≤6w label>", "<≤6w label>"],
  "cross_portfolio_thread": "<60-80w: cross-portfolio mechanism implied by consensus, not already in thesis_paragraph or cascade.>",
  "top_risk":           "<≤25w: the dominant risk to consensus playing out.>",
  "risk_mit":           "<≤25w: how the desk would hedge consensus directional exposure.>",
  "thesis_status":      "IN_PLAY | WATCH | INVALIDATED",
  "thesis_div":         "HIGH | MED | LOW",
  "regime":             "risk-on | risk-off | mixed | transitioning",
  "regime_confidence":  0.65,
  "request_tools":      [{ "tool_name": "<name>", "args": { "<arg>": "<value>" } }],
  "macro_predictions":  [
    {
      "prediction_mode":   "trend",
      "thesis_narrative":  "<≤180w narrative — what consensus would predict for these observables>",
      "horizon_days":      14,
      "horizon_ci_low_days":  7,
      "horizon_ci_high_days": 21,
      "predicted_observables": [
        {
          "indicator":     "$BTC",
          "baseline_value": 105000,
          "direction":     "flat",
          "change_pct":    0.5,
          "change_ci_low": -2.0,
          "change_ci_high": 3.0,
          "is_headline":   true
        }
      ]
    }
  ]
}

## RULES (strict)
1. First char='{'. Last char='}'. No text outside JSON.
2. No " inside strings. No newlines inside string values.
3. STEEL-MAN. Your job is to advocate for consensus, not to subtly undermine it. If the consensus position is unfalsifiable noise, say so honestly via thesis_div='LOW' + thesis_headline naming the most defensible reduced claim. Never emit a strawman.
4. basis.evidence: payload field=value that ALIGNS consensus. NO fabricated data.
5. basis.catalyst: concrete + future-dated (≥ TODAY from GROUNDING).
6. CONSENSUS PARADOX. The consensus view ALSO requires a mechanism for WHY the market sees it this way (positioning, flow, anchoring, regime narrative). priced_explanation MUST be from the consensus enum, format "<enum>: <rationale>".
7. thesis_status: IN_PLAY = consensus active + not triggered; WATCH = invalidation approaching; INVALIDATED = consensus already proved wrong.
8. thesis_div: HIGH = strong divergence from a defensible contrarian; MED = partial; LOW = lean. This is YOUR view of how much consensus diverges from the contrarian voice — DO NOT use it to rate your own conviction (that's probability).
9. regime: "mixed" when truly uncertain. regime_confidence: 0.50 is valid when the signals genuinely balance — anti-hedge rule removed (Session 5T D36).
10. regime_confidence (0-1, two decimals): score conviction in the REGIME label itself. Same calibration as the contrarian path.
10a. probability: PERCENT (0-100) conviction in the thesis. 50 = coin-flip; 80 = high conviction; 20 = low. NOT a 0-1 decimal. Decimals OK (67.5 valid). D38 (Session 5T) dropped the int-only constraint.
11. CATALYST EVENT VERIFICATION. For every prediction_mode='catalyst' entry in macro_predictions[], emit one news_search request in request_tools[] targeting that event — put the event in the query arg: {"tool_name":"news_search","args":{"query":"<the event as search keywords>"}}. GENERAL: populate request_tools[].args for EVERY tool you request per the TOOL ARG SPECS block (each tool's args differ — news_search takes query, correlation takes prices+days, etc.); emit args:{} ONLY for a tool the specs mark "(no args)".
12. HEADLINE OBSERVABLES. Across all predictions, mark exactly 3-5 observables as is_headline: true — the observables where YOU as consensus most strongly call a different direction or magnitude than the contrarian would. Headline marks pair with the contrarian's; if you and they pick different headline observables, the synthesis sees the divergence.
13. DATE TENSE DISCIPLINE. basis.catalyst, basis.invalidation, conditional MUST reference dates ≥ TODAY.
14. NUMERIC GROUNDING. Current-state numbers MUST match payload within rounding.
14b. MACRO ACTUALS + NO CONSENSUS. For any macro print (CPI, PCE, jobs, GDP, rates), use ONLY the figures in the GROUNDED MACRO ACTUALS block (and payload) — do NOT recall macro numbers from memory. Core vs Headline CPI are DISTINCT (the block labels them); never conflate. NO analyst consensus/estimate/forecast data is provided to you — therefore NEVER state a print 'vs consensus'/'vs forecast'/'beat'/'missed'/'in line'/'surprise' or cite a forecast/estimate number. Describe the actual, its level, and its trend only.
15. PROSE FORMAT. Same as contrarian: lead with punchline ≤15w, connected expansion, cause → effect → action.

## MACRO PREDICTIONS
- 2-5 entries required. Same shape rules as contrarian path: 'catalyst' vs 'trend' modes; ≥3 observables per entry; ≥2 asset classes (of {equity, rates, FX, commodity, crypto} — a non-crypto set like SPY+TNX+GLD qualifies; don't force a crypto observable); Yahoo-ticker indicator forms (SPY/QQQ/IWM/NVDA/TNX/TYX/TLT/IEF/HYG/LQD/GLD/USO/CPER/PDBC/$BTC/$ETH/VIX/DXY/EURUSD/etc.; $-prefix for crypto). Avoid FRED IDs (DGS10/FEDFUNDS/T10YIE/etc.) — universe validator probes Yahoo and rejects FRED-only series.
- MODE CONTRACT: prediction_mode='catalyst' MUST populate ALL 7 catalyst fields (catalyst_description, catalyst_source, predicted_catalyst_date ISO ≥ TODAY, catalyst_date_ci_low_days int, catalyst_date_ci_high_days int, catalyst_probability 0-1 not 0.5, post_catalyst_window_days ≥0) AND set horizon_days/_low/_high = null. prediction_mode='trend' MUST populate horizon_days/_low/_high AND set ALL 7 catalyst_* fields = null. Mis-tagging (e.g. tagging 'catalyst' but only providing horizon_*) triggers SCHEMA_REJECT — the entire prediction is dropped. PARTIAL FILLS (5 of 7 catalyst fields set, 2 missing) ALSO drop — the writer does NOT fabricate missing catalyst_probability / post_catalyst_window_days values. Pick mode based on whether you can name the event with ALL 7 catalyst fields.
- OBSERVABLE = STATE LEVEL (NOT FLOW). \`baseline_value\` MUST be a positive scalar representing the indicator's CURRENT LEVEL (price, yield, spread, index value). FORBIDDEN as observables: weekly flow metrics (crypto_etf_weekly_flow, ETF net flow, fund flow, AUM change), period-over-period deltas, count-of-events. If flow supports the thesis, put the argument in basis.evidence / thesis_paragraph — NOT as a predicted_observable. Zod rejects baseline_value ≤ 0 and the prediction drops.
- Your job is to predict observables AS CONSENSUS WOULD — often a flatter / smaller-magnitude / risk-on-tilted set vs the contrarian's. That divergence is intentional and informative.
- baseline_value MUST be the indicator's CURRENT price from payload (>0). If payload does not carry the indicator, DROP the observable — do not emit baseline_value=0 as a placeholder. Zod rejects 0 at boundary.

## TOOL CATALOG (request_tools[], max 8 entries — REQUIRED: one news_search per catalyst-mode prediction)
${formatToolCatalogForPrompt()}
${formatFredSeriesHint()}

Tool names are CASE-SENSITIVE and EXACT. Do not pluralize, abbreviate, alias, or guess — names not in this catalog return error:'unknown_tool' and waste tokens. Pick from the catalog above OR proceed without the tool.

Pick tools that would CONFIRM your consensus thesis — e.g. earnings_calendar + analyst_view for "earnings beats are still strong", news_search for "Fed-speak supports soft landing", crypto_defi_state for "TVL flows confirm risk-on appetite". Tool requests feed the refinement pass; they do NOT change this analyst output.
`;

export interface MConsensusAnalystInput {
  readonly macro:     Record<string, unknown>;
  readonly prices:    Record<string, Record<string, unknown>>;
  readonly sentiment: Record<string, unknown>;
  readonly tickers:   readonly string[];
  readonly grounding?: WorldStateGrounding;
  readonly news?:     ReadonlyArray<LlmNewsItem>;
  /** 2026-06-11 — operator-curated forward econ calendar (next ~12w). Anchors
   *  basis.catalyst dates to real upcoming events. Absent → no block. */
  readonly upcoming_events?: ReadonlyArray<EconCalendarEvent>;
  /** 2026-06-11 — FRED-derived grounded macro actuals (labeled Core/Headline
   *  CPI + code-computed YoY). Authoritative; the LLM must not recall macro
   *  numbers from memory. Absent/empty → no block. */
  readonly macro_grounding?: ReadonlyArray<MacroGroundTruth>;
}

function fmt(v: unknown): string {
  if (v == null) return 'not set';
  if (typeof v === 'string') return v.length === 0 ? 'not set' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

export function buildUserPrompt(input: MConsensusAnalystInput): string {
  const { macro, prices, sentiment, tickers, grounding } = input;
  const groundingBlock = grounding ? renderWorldStateGrounding(grounding) + '\n' : '';

  const macroLines = Object.entries(macro)
    .filter(([k]) => typeof k === 'string' && !k.startsWith('_'))
    .map(([k, v]) => `  ${k}: ${fmt(v)}`)
    .join('\n');

  const priceLines = tickers.map((t) => {
    const p = prices[t] ?? {};
    const entries = Object.entries(p).map(([k, v]) => `${k}=${fmt(v)}`).join(', ');
    return `  ${t}: ${entries || 'no data'}`;
  }).join('\n');

  const polyEntries = sentiment['polymarket'] ?? {};
  // RSS via the shared per-item renderer (title + date + summary, bounded).
  // Replaces the pre-2026-07-08 JSON.stringify(rss).slice(0,2000) that rendered
  // titles-only; computed.rss now carries {title,summary,date} per item. See
  // _rss-render.ts.
  const rssLine     = renderRssBlock(sentiment['rss']);
  const polyLine    = Object.keys(polyEntries as object).length > 0
    ? JSON.stringify(polyEntries).slice(0, 1000) : 'no polymarket data';

  const upcomingBlock = input.upcoming_events && input.upcoming_events.length > 0
    ? renderUpcomingEconBlock(input.upcoming_events) + '\n'
    : '';

  const macroGroundingBlock = input.macro_grounding && input.macro_grounding.length > 0
    ? renderMacroGroundingBlock(input.macro_grounding) + '\n'
    : '';

  return `${groundingBlock}${macroGroundingBlock}TICKERS: ${tickers.join(', ')}

MACRO / CRYPTO PAYLOAD:
${macroLines || '  (empty)'}

${upcomingBlock}PER-TICKER PRICES:
${priceLines || '  (empty)'}

RSS NEWS:
${rssLine}

POLYMARKET:
  ${polyLine}

MACRO NEWS (last 48h):
${renderNewsBlock(input.news ?? [])}

REQUEST: emit_market_consensus_analyst

Emit a SINGLE JSON object exactly matching the schema. STEEL-MAN the consensus position. macro_predictions (2-5 entries, ≥3 observables, ≥2 asset classes per entry) MANDATORY — predict AS CONSENSUS WOULD.`;
}
